import type { Component } from '@seek/data-tracking-event-catalogue';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Stack,
  Heading,
  Text,
  TextLink,
  IconTick,
  ButtonLink,
  IconMail,
} from 'braid-design-system';
import { useEffect, useRef } from 'react';

import translations from './.vocab';
import { GoogleIcon } from './images/GoogleIcon';

export interface LoginPromptProps {
  loginUrl: string;
  googleLoginUrl: string;
  registerUrl: string;
  headingText?: string;
  loginBulletPoints?: string[];
  trackingCallbacks: {
    onComponentDisplayed: (data: { component: Component }) => void;
    onAuthLinkPressed: (data: { component: Component }) => void;
    onLoginPressed: (data: {
      component: Component;
      identityProvider?: string;
    }) => void;
  };
  alignContent?: 'center' | 'left';
}

export const GenericLoginPrompt = ({
  loginUrl,
  registerUrl,
  googleLoginUrl,
  headingText,
  loginBulletPoints,
  trackingCallbacks,
  alignContent = 'center',
}: LoginPromptProps) => {
  const { t } = useTranslations(translations);
  const hasTrackedDisplayRef = useRef(false);

  useEffect(() => {
    if (!hasTrackedDisplayRef.current) {
      trackingCallbacks.onComponentDisplayed({
        component: {
          name: 'sign-in_call-to-action',
          type: 'dialog',
        },
      });
      hasTrackedDisplayRef.current = true;
    }
  }, [trackingCallbacks]);

  return (
    <Box maxWidth="xsmall" style={{ margin: 'auto' }}>
      <Stack space="large" align={{ mobile: 'center', desktop: alignContent }}>
        <Heading level="2">{headingText}</Heading>
        <Stack space="small">
          {loginBulletPoints?.map((bulletPoint) => (
            <Text
              icon={<IconTick tone="positive" />}
              align="left"
              key={bulletPoint}
            >
              {bulletPoint}
            </Text>
          ))}
        </Stack>

        <Box width="full">
          <Stack align={alignContent} space="xsmall">
            <ButtonLink
              href={googleLoginUrl}
              role="link"
              onClick={() =>
                trackingCallbacks.onLoginPressed({
                  identityProvider: 'google',
                  component: {
                    name: 'sign_in',
                    type: 'button',
                  },
                })
              }
            >
              <Box position="absolute" left={0} marginLeft="small">
                <GoogleIcon />
              </Box>
              <Box paddingLeft="small">{t('CONTINUE_WITH_GOOGLE')}</Box>
            </ButtonLink>
            <ButtonLink
              href={loginUrl}
              role="link"
              onClick={() =>
                trackingCallbacks.onAuthLinkPressed({
                  component: {
                    name: 'sign-in_navigation',
                    type: 'button',
                  },
                })
              }
            >
              <Box position="absolute" left={0} marginLeft="small">
                <IconMail />
              </Box>
              <Box paddingLeft="small">{t('CONTINUE_WITH_EMAIL')}</Box>
            </ButtonLink>
          </Stack>
        </Box>

        <Box style={{ alignSelf: 'center' }}>
          <Text>
            {t('DONT_HAVE_AN_ACCOUNT_REGISTER', {
              Link: (children) => (
                <TextLink
                  href={registerUrl}
                  role="link"
                  onClick={() =>
                    trackingCallbacks.onAuthLinkPressed({
                      component: {
                        name: 'register_navigation',
                        type: 'textLink',
                      },
                    })
                  }
                >
                  {children}
                </TextLink>
              ),
            })}
          </Text>
        </Box>
      </Stack>
    </Box>
  );
};
